import { themeVariants } from 'style-utils/theme';
import styled, { css } from 'styled-components';
import {
  background,
  BackgroundProps,
  border,
  BorderProps,
  borderRadius,
  BorderRadiusProps,
  flexbox,
  FlexboxProps,
  layout,
  LayoutProps,
  position,
  PositionProps,
  space,
  SpaceProps,
  typography,
  TypographyProps,
} from 'styled-system';

export type ButtonProps = LayoutProps &
  SpaceProps &
  FlexboxProps &
  BorderProps &
  BorderRadiusProps &
  PositionProps &
  TypographyProps &
  BackgroundProps & {
    variant?:
      | 'default'
      | 'outline'
      | 'rounded'
      | 'roundedOutline'
      | 'ghost'
      | 'white';
  } & { buttonSize?: 'default' };

const defaultCss = css`
  border-radius: var(--component-button-size-border-radius);
  border: var(--component-button-size-border-width) solid
    var(--component-button-color-primary-border-default);
  background: var(--component-button-color-primary-bg-default);

  color: var(--btn-primary-text);

  &:disabled {
    border: var(--component-button-size-border-width) solid
      var(--component-button-color-primary-border-disabled);
    background: var(--component-button-color-primary-bg-disabled);
  }

  &:hover {
    border: var(--component-button-size-border-width) solid
      var(--component-button-color-primary-border-hover);
    background: var(--component-button-color-primary-bg-hover);
  }

  &:active {
    border: var(--component-button-size-border-width) solid
      var(--component-button-color-primary-border-pressed);
    background: var(--component-button-color-primary-bg-pressed);
  }
`;

const outlinedCss = css`
  border-radius: var(--component-button-size-border-radius);

  border: var(--component-button-size-border-width) solid
    var(--component-button-color-secondary-border-default);
  background: var(--component-button-color-secondary-bg-default);

  color: var(--btn-outlined-text);

  &:hover {
    border: var(--component-button-size-border-width) solid
      var(--component-button-color-secondary-border-hover);
    background: var(--component-button-color-secondary-bg-hover);
  }

  &:active {
    border: var(--component-button-size-border-width) solid
      var(--component-button-color-secondary-border-pressed);
    background: var(--component-button-color-secondary-bg-pressed);
  }
`;

const ghostCss = css`
  border: none;
  background: transparent;
  color: inherit;
`;

const roundedCss = css`
  ${defaultCss}
  border: none;
  border-radius: 9999px;
`;

const roundedOutlineCss = css`
  border-radius: 9999px;
  ${outlinedCss}
`;
const whiteCss = css`
  color: var(--btn-primary-text);
  background: white;
  border: 1px solid #000000;
`;

const buttonVariants = themeVariants('vendor', 'variant', {
  default: {
    pokerstars: defaultCss,
  },
  outline: { pokerstars: outlinedCss },
  rounded: { pokerstars: roundedCss },
  roundedOutline: { pokerstars: roundedOutlineCss },
  ghost: { pokerstars: ghostCss },
  white: {
    pokerstars: whiteCss,
  },
});

const defaultPsSize = css`
  height: 36px;
  padding: 6px 16px;
`;

const buttonSizes = themeVariants('vendor', 'buttonSize', {
  default: {
    pokerstars: defaultPsSize,
  },
});

const Button = styled.button<ButtonProps>`
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.025em;
  font-weight: bold;

  border-style: solid;

  cursor: pointer;

  &:disabled {
    pointer-events: none;
    opacity: 0.4;
  }

  ${buttonVariants}
  ${buttonSizes}

  ${layout}
  ${space}
  ${flexbox}
  ${border}
  ${borderRadius}
  ${position}
  ${typography}
  ${background}
`;

Button.defaultProps = {
  variant: 'default',
  buttonSize: 'default',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'var(--btn-radius)',
  type: 'button',
};

export { Button };
